import React, { useState, useEffect, useCallback } from 'react';

interface Rule {
  id: string;
  vendor: string;
  source_keyword: string;
  target_name: string;
}

function authHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionStorage.getItem('admin-token') || ''}` };
}

export default function ProductRulesTab({ vendor }: { vendor: string }) {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [newSource, setNewSource] = useState('');
  const [newTarget, setNewTarget] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSource, setEditSource] = useState('');
  const [editTarget, setEditTarget] = useState('');
  const [toast, setToast] = useState('');

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  const loadRules = useCallback(() => {
    fetch(`/api/product-rules?vendor=${encodeURIComponent(vendor)}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => { setRules(Array.isArray(d) ? d : []); setLoading(false); });
  }, [vendor]);

  useEffect(() => { loadRules(); }, [loadRules]);

  const addRule = async () => {
    if (!newSource || !newTarget) { alert('원본 키워드와 치환 상품명을 입력해주세요'); return; }
    await fetch('/api/product-rules', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ vendor, source_keyword: newSource, target_name: newTarget }),
    });
    setNewSource('');
    setNewTarget('');
    loadRules();
    showToast('규칙이 추가되었습니다');
  };

  const saveEdit = async (id: string) => {
    await fetch(`/api/product-rules/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ source_keyword: editSource, target_name: editTarget }),
    });
    setEditingId(null);
    loadRules();
    showToast('규칙이 수정되었습니다');
  };

  const deleteRule = async (id: string) => {
    if (!window.confirm('삭제할까요?')) return;
    await fetch(`/api/product-rules/${id}`, { method: 'DELETE', headers: authHeaders() });
    loadRules();
    showToast('규칙이 삭제되었습니다');
  };

  const inputCls = 'px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown focus:border-jeju-brown';

  if (loading) return <div className="text-gray-400 text-sm p-8 text-center">로딩 중...</div>;

  return (
    <div>
      {/* 추가 폼 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">치환 규칙 추가</h3>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1 font-medium">원본 키워드 (스마트스토어 옵션정보에 포함된 텍스트)</label>
            <input value={newSource} onChange={e => setNewSource(e.target.value)} className={`${inputCls} w-full`} placeholder="예: 달콤맛, 왕특" />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1 font-medium">치환 상품명 (주문서에 표시될 이름)</label>
            <input value={newTarget} onChange={e => setNewTarget(e.target.value)} className={`${inputCls} w-full`} placeholder="예: [신칠라] 왕특 (18mm이상)" />
          </div>
          <button onClick={addRule} className="px-5 py-2 bg-jeju-brown text-white rounded-lg text-sm font-medium hover:bg-jeju-brown-dark transition-colors whitespace-nowrap">추가</button>
        </div>
        <p className="text-xs text-gray-400 mt-2">원본 키워드의 모든 단어가 옵션정보에 포함되면 해당 상품명으로 치환됩니다. (쉼표로 여러 키워드 지정)</p>
      </div>

      {/* 규칙 목록 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm text-gray-500">등록된 규칙: <strong className="text-gray-800">{rules.length}개</strong></span>
        </div>
        <div className="overflow-auto max-h-[calc(100vh-420px)]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">원본 키워드</th>
                <th className="text-center px-2 py-3 text-xs text-gray-400 border-b border-gray-200">→</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">치환 상품명</th>
                <th className="px-4 py-3 border-b border-gray-200 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rules.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                  {editingId === r.id ? (
                    <>
                      <td className="px-3 py-2"><input value={editSource} onChange={e => setEditSource(e.target.value)} className={`${inputCls} w-full`} /></td>
                      <td className="text-center text-gray-300">→</td>
                      <td className="px-3 py-2"><input value={editTarget} onChange={e => setEditTarget(e.target.value)} className={`${inputCls} w-full`} /></td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button onClick={() => saveEdit(r.id)} className="text-green-600 text-xs font-medium mr-2">저장</button>
                        <button onClick={() => setEditingId(null)} className="text-gray-400 text-xs">취소</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-2.5 text-gray-800 font-medium">{r.source_keyword}</td>
                      <td className="text-center text-gray-300">→</td>
                      <td className="px-4 py-2.5 text-gray-700">{r.target_name}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <button onClick={() => { setEditingId(r.id); setEditSource(r.source_keyword); setEditTarget(r.target_name); }} className="text-jeju-brown text-xs font-medium mr-2">수정</button>
                        <button onClick={() => deleteRule(r.id)} className="text-red-400 text-xs font-medium">삭제</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {rules.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">등록된 치환 규칙이 없습니다</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-jeju-brown text-white px-6 py-2.5 rounded-lg text-sm font-semibold z-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
